/** O aviso curto de que a ação aconteceu.
 *
 * O app não tinha nenhum: pagar uma fatura, confirmar o valor de uma conta variável ou
 * excluir uma recorrência acontecia em silêncio, e a única prova era o número mudar em
 * algum lugar da tela. Quando a ação e o efeito estão longe um do outro — pagar a fatura
 * no cartão muda o saldo lá na tela Hoje — o silêncio vira dúvida.
 *
 * Não é notificação: some sozinho, não empilha mais que três, e nunca cobre a barra de
 * navegação. Erro fica até ser dispensado, porque erro que some sozinho é erro que
 * ninguém leu.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "./Icon";

type Tom = "ok" | "erro";

interface Aviso {
  id: number;
  texto: string;
  tom: Tom;
  desfazer?: () => void;
}

interface ToastApi {
  ok: (texto: string, desfazer?: () => void) => void;
  erro: (texto: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

/** Fora de um provider o app não deve quebrar: sem tela para avisar, o aviso vira ruído
 *  no console e a ação segue normal. */
const SEM_PROVIDER: ToastApi = {
  ok: (t) => console.info("[aviso]", t),
  erro: (t) => console.warn("[aviso]", t),
};

export function useToast(): ToastApi {
  return useContext(Ctx) ?? SEM_PROVIDER;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const seq = useRef(0);

  const remover = useCallback((id: number) => {
    setAvisos((v) => v.filter((a) => a.id !== id));
  }, []);

  const api = useMemo<ToastApi>(() => {
    const empilhar = (texto: string, tom: Tom, desfazer?: () => void) => {
      const id = ++seq.current;
      // três é o teto: acima disso vira parede e ninguém lê nenhum
      setAvisos((v) => [...v.slice(-2), { id, texto, tom, desfazer }]);
    };
    return {
      ok: (texto, desfazer) => empilhar(texto, "ok", desfazer),
      erro: (texto) => empilhar(texto, "erro"),
    };
  }, []);

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {avisos.map((a) => (
          <Aviso key={a.id} aviso={a} onFechar={() => remover(a.id)} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function Aviso({ aviso, onFechar }: { aviso: Aviso; onFechar: () => void }) {
  useEffect(() => {
    // erro não some sozinho: erro que some sozinho é erro que ninguém leu
    if (aviso.tom === "erro") return;
    const t = setTimeout(onFechar, aviso.desfazer ? 7000 : 3600);
    return () => clearTimeout(t);
  }, [aviso, onFechar]);

  return (
    <div className={`toast ${aviso.tom}`}>
      <span className="t">{aviso.texto}</span>
      {aviso.desfazer && (
        <button
          type="button"
          className="undo"
          onClick={() => {
            aviso.desfazer?.();
            onFechar();
          }}
        >
          Desfazer
        </button>
      )}
      <button type="button" className="x" onClick={onFechar} aria-label="Fechar aviso">
        <Icon name="x" className="ix" />
      </button>
    </div>
  );
}
